/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Message, Spin } from '@arco-design/web-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { isMobile } from '@/utils/utils';
import { leaveCardView } from '@/store/slices/room';
import { RootState } from '@/store';
import request from '@/utils/request';
import API_CONFIG from '@/config/api';
import style from './index.module.less';

// 类型定义
interface DashboardStatistics {
  total_warnings: number;
  total_sessions: number;
  urgent_warnings: number;
  avg_score_change: number;
}

interface EmotionDistribution {
  low: number;
  medium: number;
  high: number;
}

interface WarningSeverityDistribution {
  high: number;
  medium: number;
  low: number;
}

interface RecentWarning {
  id: number;
  type: string;
  severity: 'high' | 'medium' | 'low';
  score: number;
  message: string;
  timestamp: string;
  session_id: string;
}

interface DashboardData {
  statistics: DashboardStatistics;
  emotion_distribution: EmotionDistribution;
  warning_severity_distribution: WarningSeverityDistribution;
  recent_warnings: RecentWarning[];
}

interface DashboardResponse {
  code: number;
  message: string;
  data: DashboardData;
}

type ViewState = 'loading' | 'ready' | 'error' | 'empty';

// 颜色配置
const EMOTION_COLORS = {
  positive: '#00d4ff',  // 积极 - 青色
  negative: '#ff6b8a',  // 消极 - 粉红色
  neutral: '#a855f7',   // 中性 - 紫色
};

const SEVERITY_COLORS = {
  high: '#ff6b8a',    // 高 - 粉红色
  medium: '#fbbf24',  // 中 - 黄色
  low: '#4ade80',     // 低 - 绿色
};

function EmotionReport() {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [data, setData] = useState<DashboardData | null>(null);

  // 加载仪表盘数据
  const loadDashboardData = useCallback(async () => {
    setViewState('loading');
    try {
      const response = await request.get<DashboardResponse>(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BOARD_DATA}`,
        {
          params: {
            username: auth.username || '',
          },
        }
      );

      if (response.code === 200 && response.data) {
        setData(response.data);
        setViewState('ready');
      } else {
        console.error('获取仪表盘数据失败:', response.message);
        setViewState('error');
      }
    } catch (error) {
      console.error('获取仪表盘数据失败:', error);
      setViewState('error');
    }
  }, [auth.username]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleBack = () => {
    dispatch(leaveCardView());
  };

  // 导出预警数据
  const handleExport = () => {
    if (!data?.recent_warnings) return;
    
    const csvContent = [
      ['ID', '类型', '严重程度', '得分', '消息', '时间', '会话ID'].join(','),
      ...data.recent_warnings.map(w => [
        w.id,
        w.type,
        w.severity,
        w.score,
        `"${w.message}"`,
        w.timestamp,
        w.session_id,
      ].join(','))
    ].join('\n');

    const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `emotion_warnings_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    Message.success('导出成功');
  };

  // 准备情绪分布图数据
  const getEmotionChartData = () => {
    if (!data?.emotion_distribution) return [];
    const { low, medium, high } = data.emotion_distribution;
    return [
      { name: '积极', value: high, color: EMOTION_COLORS.positive },
      { name: '消极', value: low, color: EMOTION_COLORS.negative },
      { name: '中性', value: medium, color: EMOTION_COLORS.neutral },
    ].filter(item => item.value > 0);
  };

  // 准备预警严重程度分布图数据
  const getSeverityChartData = () => {
    if (!data?.warning_severity_distribution) return [];
    const { high, medium, low } = data.warning_severity_distribution;
    return [
      { name: '高', value: high, fill: SEVERITY_COLORS.high },
      { name: '中', value: medium, fill: SEVERITY_COLORS.medium },
      { name: '低', value: low, fill: SEVERITY_COLORS.low },
    ];
  };

  // 获取严重程度标签样式
  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case 'high': return style.severityHigh;
      case 'medium': return style.severityMedium;
      case 'low': return style.severityLow;
      default: return '';
    }
  };

  // 获取严重程度中文名
  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return severity;
    }
  };

  // Loading State
  if (viewState === 'loading') {
    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>←</button>
          <h2 className={style.title}>数据分析仪表盘</h2>
          <div className={style.headerNav}>
            <button className={style.navButton} onClick={handleBack}>🏠 首页</button>
          </div>
        </div>
        <div className={style.content}>
          <div className={style.loadingState}>
            <Spin size={40} />
            <p>加载中...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (viewState === 'error') {
    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>←</button>
          <h2 className={style.title}>数据分析仪表盘</h2>
          <div className={style.headerNav}>
            <button className={style.navButton} onClick={handleBack}>🏠 首页</button>
          </div>
        </div>
        <div className={style.content}>
          <div className={style.errorState}>
            <div className={style.errorIcon}>⚠️</div>
            <h3>加载失败</h3>
            <p>无法获取仪表盘数据，请稍后重试</p>
            <button className={style.retryButton} onClick={loadDashboardData}>
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ready State
  if (!data) return null;

  const emotionChartData = getEmotionChartData();
  const severityChartData = getSeverityChartData();

  return (
    <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
      {/* Header */}
      <div className={style.header}>
        <button className={style.backButton} onClick={handleBack}>←</button>
        <h2 className={style.title}>数据分析仪表盘</h2>
        <div className={style.headerNav}>
          <button className={style.navButton} onClick={handleBack}>🏠 首页</button>
        </div>
      </div>

      {/* Content */}
      <div className={style.content}>
        {/* Statistics Cards */}
        <div className={style.statsGrid}>
          <div className={style.statCard}>
            <div className={style.statHeader}>
              <span className={style.statLabel}>总对话数</span>
              <span className={style.statIcon}>💬</span>
            </div>
            <div className={style.statValue} style={{ color: '#00d4ff' }}>
              {data.statistics.total_sessions}
            </div>
          </div>

          <div className={style.statCard}>
            <div className={style.statHeader}>
              <span className={style.statLabel}>总消息数</span>
              <span className={style.statIcon}>📈</span>
            </div>
            <div className={style.statValue} style={{ color: '#00d4ff' }}>
              {data.statistics.total_warnings}
            </div>
          </div>

          <div className={style.statCard}>
            <div className={style.statHeader}>
              <span className={style.statLabel}>触发预警数</span>
              <span className={style.statIcon}>⚠️</span>
            </div>
            <div className={style.statValue} style={{ color: '#ff6b8a' }}>
              {data.statistics.urgent_warnings}
            </div>
          </div>

          <div className={style.statCard}>
            <div className={style.statHeader}>
              <span className={style.statLabel}>平均情绪分数</span>
              <span className={style.statIcon}>📊</span>
            </div>
            <div className={style.statValue} style={{ color: data.statistics.avg_score_change >= 0 ? '#4ade80' : '#ff6b8a' }}>
              {data.statistics.avg_score_change >= 0 ? '+' : ''}{data.statistics.avg_score_change.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className={style.chartsGrid}>
          {/* Emotion Distribution - Pie Chart */}
          <div className={style.chartCard}>
            <h3 className={style.chartTitle}>
              <span className={style.chartIcon}>😊</span>
              情绪分布
            </h3>
            <div className={style.chartContainer}>
              {emotionChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={emotionChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {emotionChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: 'rgba(0, 0, 0, 0.9)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#fff',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className={style.noData}>暂无数据</div>
              )}
              <div className={style.chartLegend}>
                <div className={style.legendItem}>
                  <span className={style.legendDot} style={{ background: EMOTION_COLORS.positive }} />
                  <span>积极: {data.emotion_distribution.high}</span>
                </div>
                <div className={style.legendItem}>
                  <span className={style.legendDot} style={{ background: EMOTION_COLORS.negative }} />
                  <span>消极: {data.emotion_distribution.low}</span>
                </div>
                <div className={style.legendItem}>
                  <span className={style.legendDot} style={{ background: EMOTION_COLORS.neutral }} />
                  <span>中性: {data.emotion_distribution.medium}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Severity Distribution - Bar Chart */}
          <div className={style.chartCard}>
            <h3 className={style.chartTitle}>
              <span className={style.chartIcon}>⚠️</span>
              预警严重程度分布
            </h3>
            <div className={style.chartContainer}>
              {severityChartData.some(item => item.value > 0) ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={severityChartData} barSize={60}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis
                      dataKey="name"
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
                    />
                    <YAxis
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'rgba(0, 0, 0, 0.9)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#fff',
                      }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {severityChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={style.noData}>暂无数据</div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Warnings Section */}
        <div className={style.warningsSection}>
          <div className={style.sectionHeader}>
            <h3 className={style.sectionTitle}>
              <span className={style.sectionIcon}>⚠️</span>
              最近预警
            </h3>
            <button className={style.exportButton} onClick={handleExport}>
              📥 导出
            </button>
          </div>
          <div className={style.warningsList}>
            {data.recent_warnings.length > 0 ? (
              data.recent_warnings.map((warning) => (
                <div key={warning.id} className={style.warningItem}>
                  <div className={style.warningLeft}>
                    <span className={`${style.severityBadge} ${getSeverityClass(warning.severity)}`}>
                      {getSeverityLabel(warning.severity)}
                    </span>
                    <span className={style.warningType}>{warning.type}</span>
                  </div>
                  <div className={style.warningCenter}>
                    <span className={style.warningMessage}>{warning.message}</span>
                  </div>
                  <div className={style.warningRight}>
                    <span className={style.warningTime}>{warning.timestamp}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className={style.noWarnings}>暂无预警记录</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmotionReport;
